import { __spreadArrays } from "tslib";
import { LIST_DROPDOWN_CONSTANTS } from './list-dropdown-constants';
import { createDropdown, createList, createListItems, createAsyncElement, createBusyElement, createCheckboxElement, buildDialogContent } from './list-dropdown-utils';
import { POPUP_CONSTANTS } from '../popup';
import { LIST_CONSTANTS } from '../list/list';
import { createVisuallyHiddenElement, isDeepEqual, tryScrollIntoView } from '../core';
import { LIST_ITEM_CONSTANTS } from '../list/list-item';
import { ScrollEvents, getShadowElement, ScrollAxisObserver, removeAllChildren, isFunction, removeElement, replaceElement } from '@tyler-components-web/core';
import { ICON_CONSTANTS } from '../icon';
import { SCAFFOLD_CONSTANTS } from '../scaffold';
var ListDropdownAdapter = /** @class */ (function () {
    function ListDropdownAdapter(_targetElement) {
        this._targetElement = _targetElement;
    }
    Object.defineProperty(ListDropdownAdapter.prototype, "dropdownElement", {
        get: function () {
            return this._dropdownElement;
        },
        enumerable: true,
        configurable: true
    });
    ListDropdownAdapter.prototype.open = function (config, selectCallback, isMobile, closeCb) {
        // Now lets create the popup and append the children
        this._dropdownElement = createDropdown(config, this._targetElement, isMobile);
        if (!isMobile) {
            this.syncWidth(!!config.syncWidth, config.targetWidthCallback);
        }
        // If we are configured to show a busy indicator (linear progress bar across the top), then create and append it first
        if (config.allowBusy) {
            this._busyElement = createBusyElement();
            this._busyElement.style.display = 'none';
            this._dropdownElement.appendChild(this._busyElement);
        }
        // Create the header element if a builder exists
        if (config.headerBuilder) {
            this._headerElement = config.headerBuilder();
        }
        // Create the footer element if a builder exists
        if (config.footerBuilder) {
            this._footerElement = config.footerBuilder();
        }
        // Create the list from our config
        this._listElement = createList(config);
        // Add the listener for when list items are selected from the dropdown
        this._listElement.addEventListener(LIST_ITEM_CONSTANTS.events.SELECTED, function (evt) {
            evt.detail.listItem.setAttribute('aria-selected', 'true');
            selectCallback(evt.detail.value, evt.detail.listItem.id);
        });
        // Create the dialog container element if on mobile screen size
        if (isMobile) {
            this._dialogContent = buildDialogContent(config, this._listElement, closeCb);
        }
        // Determine if we need to show the list or the async element first
        if (config.options.length) {
            // Now we can create an append the list items
            createListItems(config, this._listElement);
            if (isMobile) {
                this._dropdownElement.appendChild(this._dialogContent);
            }
            else {
                this._dropdownElement.appendChild(this._listElement);
            }
            // Always append the optional header element **first**
            if (this._headerElement) {
                if (isMobile) {
                    var container = this._dropdownElement.querySelector(SCAFFOLD_CONSTANTS.elementName);
                    this._headerElement.slot = 'header';
                    container.appendChild(this._headerElement);
                }
                else {
                    this._dropdownElement.insertAdjacentElement('afterbegin', this._headerElement);
                }
            }
            // Always append the optional footer element **last**
            if (this._footerElement) {
                if (isMobile) {
                    var container = this._dropdownElement.querySelector(SCAFFOLD_CONSTANTS.elementName);
                    this._footerElement.slot = 'footer';
                    container.appendChild(this._footerElement);
                }
                else {
                    this._dropdownElement.appendChild(this._footerElement);
                }
            }
        }
        else if (config.allowBusy) {
            this._asyncElement = createAsyncElement(config.asyncStyle);
            this._dropdownElement.appendChild(this._asyncElement);
        }
        // Create the announcer element for a11y
        this._announcerElement = createVisuallyHiddenElement();
        this._announcerElement.id = config.id + "-activedescendant";
        this._dropdownElement.appendChild(this._announcerElement);
        // Open the popup
        this._dropdownElement.open = true;
    };
    ListDropdownAdapter.prototype.close = function () {
        if (!this._dropdownElement) {
            return;
        }
        this._dropdownElement.open = false;
        this._dropdownElement = undefined;
        this._listElement = undefined;
        this._announcerElement = undefined;
        this._dialogContent = undefined;
    };
    ListDropdownAdapter.prototype.setScrollBottomListener = function (listener, scrollThreshold) {
        if (this._dropdownElement) {
            if (!this._scrollObserver) {
                var scrollTarget = getShadowElement(this._dropdownElement, POPUP_CONSTANTS.selectors.CONTAINER);
                var scrollConfig = { scrollThreshold: scrollThreshold };
                this._scrollObserver = new ScrollAxisObserver(scrollTarget, scrollConfig);
                this._scrollObserver.addListener(ScrollEvents.ScrolledEnd, listener);
            }
        }
    };
    ListDropdownAdapter.prototype.removeScrollBottomListener = function (listener) {
        if (this._scrollObserver) {
            this._scrollObserver.removeListener(ScrollEvents.ScrolledEnd, listener);
        }
    };
    ListDropdownAdapter.prototype.getActiveOptionIndex = function () {
        if (!this._dropdownElement) {
            return -1;
        }
        var listItems = this._getListItemElements();
        var activeListItem = __spreadArrays(listItems).reverse().find(function (li) { return li.active; });
        return activeListItem ? listItems.indexOf(activeListItem) : -1;
    };
    ListDropdownAdapter.prototype.getActiveOptionIdByIndex = function (index) {
        if (!this._dropdownElement) {
            return null;
        }
        var listItems = this._getListItemElements();
        var item = listItems[index];
        return item ? item.id : null;
    };
    ListDropdownAdapter.prototype.toggleOptionMultiple = function (index, isSelected) {
        if (!this._dropdownElement) {
            return;
        }
        var listItems = this._getListItemElements();
        if (listItems.length && listItems[index]) {
            this._toggleSelectedOption(listItems[index], isSelected);
        }
    };
    ListDropdownAdapter.prototype.scrollSelectedOptionIntoView = function (animate) {
        if (animate === void 0) { animate = true; }
        var listItem = this._getSelectedListItem();
        this._scrollListItemIntoView(listItem, animate ? 'smooth' : 'auto', 'center');
    };
    ListDropdownAdapter.prototype.activateSelectedOption = function (config) {
        var listItems = this._getListItemElements();
        if (listItems.length) {
            var activeListItems = listItems.filter(function (li) { return li.active; });
            activeListItems.forEach(function (li) { return li.active = false; });
        }
        var listItem = this._getSelectedListItem();
        if (listItem) {
            this._activateListOption(listItem, config.activeChangeCallback);
        }
    };
    ListDropdownAdapter.prototype.activateOption = function (index, activeChangeCallback, animate) {
        if (animate === void 0) { animate = true; }
        if (!this._dropdownElement) {
            return;
        }
        var listItems = this._getListItemElements();
        if (listItems.length) {
            var activeListItems = listItems.filter(function (li) { return li.active; });
            activeListItems.forEach(function (li) { return li.active = false; });
            this._activateListOption(listItems[index], activeChangeCallback);
            this._scrollListItemIntoView(listItems[index], animate ? 'smooth' : 'auto');
        }
    };
    ListDropdownAdapter.prototype.setSelectedValues = function (values, multiple) {
        if (multiple === void 0) { multiple = false; }
        if (!this._dropdownElement) {
            return;
        }
        var listItems = this._getListItemElements();
        var _loop_1 = function (listItem) {
            var isSelected = values.some(function (v) { return isDeepEqual(v, listItem.value); });
            listItem.selected = isSelected;
            if (multiple) {
                var checkboxElement = listItem.querySelector("[" + LIST_DROPDOWN_CONSTANTS.attributes.CHECKBOX_ELEMENT + "]");
                if (checkboxElement) {
                    var newCheckboxElement = createCheckboxElement(isSelected);
                    replaceElement(newCheckboxElement, checkboxElement);
                }
            }
        };
        for (var _i = 0, listItems_1 = listItems; _i < listItems_1.length; _i++) {
            var listItem = listItems_1[_i];
            _loop_1(listItem);
        }
    };
    ListDropdownAdapter.prototype.clearActiveOption = function () {
        var listItems = this._getListItemElements();
        listItems.forEach(function (li) { return li.active = false; });
    };
    ListDropdownAdapter.prototype.syncWidth = function (sync, targetWidthCallback) {
        if (this._dropdownElement) {
            this._dropdownElement.style[sync ? 'width' : 'minWidth'] = this._getTargetElementWidth(targetWidthCallback) + "px";
        }
    };
    ListDropdownAdapter.prototype.setOptions = function (config) {
        if (!this._dropdownElement || !this._listElement) {
            return;
        }
        if (this._asyncElement && this._asyncElement.isConnected) {
            removeElement(this._asyncElement);
        }
        if (this._busyElement) {
            this._busyElement.close();
        }
        if (!this._listElement.isConnected) {
            this._dropdownElement.appendChild(this._listElement);
        }
        removeAllChildren(this._listElement);
        createListItems(config, this._listElement);
        if (this._headerElement && !this._headerElement.isConnected) {
            this._dropdownElement.insertAdjacentElement('afterbegin', this._headerElement);
        }
        if (this._footerElement && !this._footerElement.isConnected) {
            this._dropdownElement.insertAdjacentElement('beforeend', this._footerElement);
        }
        if (typeof this._dropdownElement.position === 'function') {
            this._dropdownElement.position();
        }
    };
    ListDropdownAdapter.prototype.appendOptions = function (options, config) {
        if (!this._dropdownElement || !this._listElement) {
            return;
        }
        createListItems(config, this._listElement, options);
    };
    ListDropdownAdapter.prototype.setBusyVisibility = function (isVisible) {
        if (!this._dropdownElement) {
            return;
        }
        if (isVisible) {
            this._busyElement.style.removeProperty('display');
            this._busyElement.open();
        }
        else {
            this._busyElement.close();
        }
    };
    ListDropdownAdapter.prototype.getScreenWidth = function () {
        return window.innerWidth;
    };
    ListDropdownAdapter.prototype._getListItemElements = function () {
        if (!this._dropdownElement) {
            return [];
        }
        var listElement = this._dropdownElement.querySelector(LIST_CONSTANTS.elementName);
        if (!listElement) {
            return [];
        }
        return Array.from(listElement.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
    };
    ListDropdownAdapter.prototype._toggleSelectedOption = function (listItem, isSelected) {
        if (!this._dropdownElement) {
            return;
        }
        // First we need to remove the active state from any other list items
        var listItems = this._getListItemElements();
        if (listItems.length) {
            var activeItems = listItems.filter(function (li) { return li !== listItem && li.active; });
            if (activeItems.length) {
                activeItems.forEach(function (ai) { return (ai.active = false); });
            }
        }
        // Now we can toggle the selected state and sync the active state
        listItem.selected = isSelected;
        listItem.setAttribute('aria-selected', "" + isSelected);
        listItem.setAttribute('aria-checked', "" + isSelected);
        if (isSelected) {
            listItem.active = true;
        }
        // Toggle the checkbox icon based on the selected state
        var checkboxElement = listItem.querySelector(ICON_CONSTANTS.elementName + "[slot=leading]");
        if (checkboxElement) {
            var newCheckboxElement = createCheckboxElement(isSelected);
            replaceElement(newCheckboxElement, checkboxElement);
        }
    };
    ListDropdownAdapter.prototype._getTargetElementWidth = function (cb) {
        if (cb && isFunction(cb)) {
            return cb();
        }
        return this._targetElement.getBoundingClientRect().width;
    };
    ListDropdownAdapter.prototype._activateListOption = function (listItem, activeChangeCallback) {
        if (listItem) {
            listItem.active = true;
            if (activeChangeCallback && isFunction(activeChangeCallback)) {
                activeChangeCallback(listItem.id);
            }
        }
    };
    ListDropdownAdapter.prototype._scrollListItemIntoView = function (listItem, behavior, block) {
        if (behavior === void 0) { behavior = 'auto'; }
        if (block === void 0) { block = 'nearest'; }
        if (listItem && this._dropdownElement && this._dropdownElement.isConnected) {
            // Our dropdown element can be either a `<tcw-popup>` or `<tcw-dialog>` so we need to locate the 
            // scrollable element based on the existence of either component
            var isPopupElement = this._dropdownElement.tagName.toLowerCase() === POPUP_CONSTANTS.elementName;
            var scrollContainer = isPopupElement ?
                getShadowElement(this._dropdownElement, POPUP_CONSTANTS.selectors.CONTAINER) :
                this._dropdownElement.querySelector(LIST_DROPDOWN_CONSTANTS.selectors.DIALOG_SCROLL_CONTAINER);
            if (scrollContainer) {
                tryScrollIntoView(scrollContainer, listItem, behavior, block);
            }
        }
    };
    ListDropdownAdapter.prototype._getSelectedListItem = function () {
        var listItems = this._getListItemElements();
        return listItems.find(function (li) { return li.selected; });
    };
    return ListDropdownAdapter;
}());
export { ListDropdownAdapter };
